/**
 * 四角色共用的佈局殼（v3 — 對齊 ZHCB design tokens）
 * ──────────────────────────────────────────────
 * 通過 BaseLayoutConfig 注入角色差異：sider 主題、Logo、菜單、插槽。
 * 設計：
 *   - Sider：collapsed 平滑動效、Logo + 副標題二段排版
 *   - Header：毛玻璃 + 全局搜尋插槽（⌘K）+ 通知鈴鐺 + 用戶卡
 *   - Content：top-padding 24，最大寬度自適應
 *
 * v3 變更：
 *   - 改用 useAuthStore (Zustand) 替代舊 useAuth (Context)
 *   - 登出調用 apiClient.post + setUser(null) + navigate
 *   - 角色從 admin/auditor/counselor 擴展為 admin/manager/decider/auditor
 */
import { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Dropdown, Avatar, Drawer } from 'antd';
import type { MenuProps } from 'antd';
import { LogOut, ChevronLeft, ChevronRight, User, Search, Menu as MenuIcon } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useRouter, usePathname } from '@/lib/navigation';
import { useAuthStore } from '@/lib/auth-context';
import { apiClient } from '@/lib/api-client';
import { findOpenKeys, findSelectedKeys } from '@/components/layout/layout-menu-config';
import type { BaseLayoutConfig } from '@/components/layout/layout-menu-config';
import { color, font, radius, space, text, tracking, weight } from '@/styles/tokens';
import { useResponsive } from '@/hooks/useResponsive';
import CommandPalette from '@/components/command-palette/CommandPalette';
import {
  useCommandPaletteShortcut,
  useCommandPaletteStore,
} from '@/components/command-palette/use-command-palette';
import NotificationDrawer from '@/components/notifications/NotificationDrawer';
import { NotificationBell } from '@/components/layout/NotificationBell';
import BulletinAnnouncementPopup from '@/components/bulletin/BulletinAnnouncementPopup';
import { VersionBadge } from '@/components/common/VersionBadge';

const { Header, Sider, Content } = Layout;

/**
 * 側邊欄常開菜單組（序115）：公佈欄、稽核專區、統計分析默認展開且不可折合；
 * 系統設置維持原有展開/折疊行為。key 不存在於當前角色菜單時由 antd 忽略，無副作用。
 */
const PINNED_OPEN_GROUP_KEYS = ['bulletin-group', 'audit-zone', 'analytics-group'];

interface BaseLayoutProps {
  config: BaseLayoutConfig;
  children: React.ReactNode;
  focusMode?: boolean;
}

export default function BaseLayout({ config, children, focusMode = false }: BaseLayoutProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);
  /**
   * 移動端 Sider 改用 Drawer 形態（off-canvas），mobileDrawerOpen 控制顯隱。
   * 桌面/Pad 仍用固定 Sider。
   */
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const currentUser = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  /**
   * 響應式：iPad 直立及以下尺寸自動收起側邊欄；
   * userToggledRef 守衛使用者手動切換後不再被自動覆寫。
   */
  const { isMobile, isPadPortrait } = useResponsive();
  const userToggledRef = useRef(false);
  useEffect(() => {
    if (userToggledRef.current) return;
    // < lg 時自動 collapsed（iPad 直立 768~991px）
    if (isPadPortrait || isMobile) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [isPadPortrait, isMobile]);

  /** 登出：清除後端 session，清除前端狀態，跳轉登入頁 */
  const logout = async () => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch {
      /* 即使後端失敗也繼續清除本地狀態 */
    }
    setUser(null);
    void navigate({ to: '/login' });
  };

  /* 註冊全域 ⌘K / Ctrl+K 開啟 CommandPalette */
  useCommandPaletteShortcut();
  const openCommandPalette = useCommandPaletteStore((s) => s.setOpen);

  /* 路徑變化時自動展開對應子菜單（常開組永遠保持展開，序115） */
  useEffect(() => {
    if (!collapsed) {
      setOpenKeys(
        Array.from(
          new Set([...PINNED_OPEN_GROUP_KEYS, ...findOpenKeys(pathname, config.menuItems)]),
        ),
      );
    }
  }, [pathname, collapsed, config.menuItems]);

  /* 路由切換時自動關閉 mobile drawer，避免使用者點完還要手動關 */
  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [pathname]);

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    router.push(key);
  };

  const handleOpenChange = (keys: string[]) => {
    // 常開組不可折疊（序115：公佈欄/稽核專區/統計分析直接展開，不折合）
    setOpenKeys(Array.from(new Set([...PINNED_OPEN_GROUP_KEYS, ...keys])));
  };

  /** 用戶下拉選單 */
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <User size={14} />,
      label: '個人設定',
      onClick: () => router.push('/me/profile'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogOut size={14} />,
      label: '登出',
      onClick: () => {
        void logout();
      },
    },
  ];

  if (!currentUser) return null;

  const { sider, logo, menuItems, headerLeftExtra, siderTopExtra, siderBottomExtra } = config;
  const isDarkSider = sider.className === 'sidebar-dark';

  /* sider 視覺 token */
  const siderBorderColor = isDarkSider ? color.sidebar.border : color.border.subtle;
  const logoBorderColor = isDarkSider ? 'rgba(255, 255, 255, 0.06)' : color.border.subtle;
  const logoTitleColor = isDarkSider ? '#ffffff' : color.text.primary;
  const logoSubtitleColor = isDarkSider ? color.sidebar.textMuted : color.text.tertiary;

  /**
   * 側邊欄內部內容（Sider 與 Drawer 共用）。
   * mode='drawer' 時忽略 collapsed（Drawer 內永遠展開），且不顯示底部收合按鈕。
   */
  const renderSiderInner = (mode: 'sider' | 'drawer') => {
    const isDrawerMode = mode === 'drawer';
    /* drawer 模式忽略 collapsed，永遠展開 */
    const showCollapsed = !isDrawerMode && collapsed;
    return (
      <div
        /**
         * drawer 模式下把 sider.className（如 sidebar-dark）掛在此內容容器上，
         * 而非 Drawer 的 rootClassName。原因：rootClassName 會套到 antd 全屏
         * fixed 的 `.ant-drawer` 根節點；而 `.sidebar-dark { background:!important }`
         * 會把該全屏根節點鋪成不透明深色，Drawer 關閉後根節點仍留在 DOM（antd6
         * 關閉態不 display:none），造成「移動端點菜單跳轉後整屏黑屏」。掛在 body
         * 內的內容容器則只給側欄內容區着色，深色菜單樣式（依賴祖先 scope）照常生效。
         * Sider 模式的着色仍由 Sider 自身的 className 負責，此處不重複。
         */
        className={isDrawerMode ? sider.className : undefined}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* ═══ Logo 區 ═══ */}
        <div
          style={{
            minHeight: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: showCollapsed ? 'center' : 'flex-start',
            padding: showCollapsed ? '14px 0' : '14px 18px',
            borderBottom: `1px solid ${logoBorderColor}`,
            backgroundColor: logo.backgroundColor,
            flexShrink: 0,
          }}
        >
          {logo.showIcon !== false && (
            <img
              src="/zhcert-logo-icon.png"
              alt="ZHCERT"
              style={{
                width: 36,
                height: 36,
                flexShrink: 0,
                objectFit: 'contain',
                filter: isDarkSider ? 'invert(1)' : 'none',
              }}
            />
          )}
          {!showCollapsed && (
            <div
              style={{
                marginLeft: logo.showIcon !== false ? 12 : 0,
                minWidth: 0,
                flex: 1,
              }}
            >
              <div
                style={{
                  fontFamily: font.display,
                  fontSize: logo.subtitle ? text.lg : text.md,
                  fontWeight: weight.bold,
                  color: logoTitleColor,
                  letterSpacing: tracking.tight,
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {logo.title}
              </div>
              {logo.subtitle && (
                <div
                  style={{
                    fontSize: text.xs,
                    color: logo.subtitleColor ?? logoSubtitleColor,
                    letterSpacing: tracking.wider,
                    textTransform: 'uppercase',
                    fontWeight: weight.semibold,
                    marginTop: 3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {logo.subtitle}
                </div>
              )}
            </div>
          )}
        </div>

        {siderTopExtra}

        {/* ═══ 菜單（可滾動區） ═══ */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBlock: space[2] }}>
          <Menu
            mode="inline"
            selectedKeys={findSelectedKeys(pathname, menuItems)}
            // ⚠️ 收合（inlineCollapsed）時不受控 openKeys：序115 常開組 key 常駐
            // openKeys，收合後 antd 會把「展開態」分組子項渲染成疊在一起的懸浮彈層
            // （縮窄視窗時公告/統計分析子項糊成一團的 bug）。收合態交回 antd 內部
            // 管理 hover 彈出；展開時恢復受控（常開組邏輯不變）。
            openKeys={showCollapsed ? undefined : openKeys}
            onOpenChange={showCollapsed ? undefined : handleOpenChange}
            items={menuItems}
            onClick={handleMenuClick}
            style={{ border: 'none', background: 'transparent' }}
            theme={isDarkSider ? 'dark' : 'light'}
            inlineCollapsed={showCollapsed}
          />
        </div>

        {siderBottomExtra}

        {/* ═══ 版本徽標（前後端版本比對，確認部署是否生效）═══ */}
        {!isDrawerMode && (
          <div style={{ borderTop: `1px solid ${logoBorderColor}`, flexShrink: 0 }}>
            <VersionBadge dark={isDarkSider} collapsed={showCollapsed} />
          </div>
        )}

        {/* ═══ 收合 / 展開觸發器（側邊欄底部）— Drawer 模式不需要 ═══ */}
        {!isDrawerMode && (
          <div
            style={{
              padding: showCollapsed ? '8px' : '8px 12px',
              borderTop: `1px solid ${logoBorderColor}`,
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              onClick={() => {
                userToggledRef.current = true;
                setCollapsed(!collapsed);
              }}
              aria-label={collapsed ? '展開側邊欄' : '收合側邊欄'}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                justifyContent: showCollapsed ? 'center' : 'flex-start',
                fontSize: text.sm,
                color: isDarkSider ? color.sidebar.textMuted : color.text.tertiary,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '8px 10px',
                borderRadius: radius.md,
                transition: 'all 120ms cubic-bezier(0.16, 1, 0.3, 1)',
                fontWeight: weight.medium,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkSider
                  ? color.sidebar.hoverBg
                  : color.surface.sunken;
                e.currentTarget.style.color = isDarkSider
                  ? color.sidebar.textActive
                  : color.text.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = isDarkSider
                  ? color.sidebar.textMuted
                  : color.text.tertiary;
              }}
            >
              {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              {!collapsed && <span>收合側邊欄</span>}
            </button>
          </div>
        )}
      </div>
    );
  };

  /**
   * 主內容區的 marginLeft：
   * - mobile：Drawer 形態，主內容無偏移
   * - 其他：固定 Sider，依照 collapsed 狀態給 72/sider.width
   */
  const mainMarginLeft = isMobile ? 0 : collapsed ? 72 : sider.width;

  /**
   * 首屏挂載門控：首次渲染不啟用主內容區的 margin-left 過渡，避免任何首幀
   * margin 變化（例如響應式斷點在首幀被纠正）被 `transition` 放大播成可見的
   * 「左右橫移一下」。挂載完成後再啟用過渡，使用者手動收合/展開側邊欄時仍
   * 保有平滑動畫。與 useResponsive 的首幀斷點兜底形成雙重防護。
   */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (focusMode) {
    return (
      <Layout style={{ minHeight: '100vh', background: color.surface.canvas }}>
        <Content
          style={{
            padding: isMobile
              ? '12px 12px 24px'
              : isPadPortrait
                ? '16px 16px 24px'
                : '20px 24px 32px',
            minHeight: '100vh',
            background: color.surface.canvas,
          }}
        >
          {children}
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* ═══════════════════════════════════════════
       * 側邊欄：mobile 用 Drawer，其它用固定 Sider
       * ═══════════════════════════════════════════ */}
      {isMobile ? (
        <Drawer
          placement="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          closeIcon={null}
          width={Math.min(sider.width + 20, 280)}
          styles={{
            body: { padding: 0, background: sider.background },
            header: { display: 'none' },
          }}
        >
          {renderSiderInner('drawer')}
        </Drawer>
      ) : (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(c) => {
            userToggledRef.current = true;
            setCollapsed(c);
          }}
          width={sider.width}
          collapsedWidth={72}
          style={{
            overflow: 'hidden',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: sider.background,
            borderRight: isDarkSider ? 'none' : `1px solid ${siderBorderColor}`,
            transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          trigger={null}
          className={sider.className}
        >
          {renderSiderInner('sider')}
        </Sider>
      )}

      {/* ═══════════════════════════════════════════
       * 主內容區
       * ═══════════════════════════════════════════ */}
      <Layout
        style={{
          marginLeft: mainMarginLeft,
          /* 首屏不過渡（見上方 mounted 註解），挂載後才啟用，避免首幀橫移 */
          transition: mounted ? 'margin-left 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          background: color.surface.canvas,
        }}
      >
        {/* ─── 頂部 Header ─── */}
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: 'rgba(255, 255, 255, 0.78)',
            backdropFilter: 'blur(16px) saturate(180%)',
            WebkitBackdropFilter: 'blur(16px) saturate(180%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${color.border.subtle}`,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            height: 56,
            lineHeight: '56px',
          }}
        >
          {/* 左側：[mobile 漢堡] + ⌘K 命令面板 + 自定義插槽 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: space[3],
              minWidth: 0,
              flex: 1,
            }}
          >
            {/* mobile 漢堡按鈕：開啟側邊抽屜 */}
            {isMobile && (
              <button
                type="button"
                onClick={() => setMobileDrawerOpen(true)}
                aria-label="開啟主選單"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: radius.md,
                  color: color.text.secondary,
                }}
              >
                <MenuIcon size={20} />
              </button>
            )}

            {/* ⌘K 全域命令面板觸發按鈕（mobile 上只顯示 icon） */}
            <button
              type="button"
              onClick={() => openCommandPalette(true)}
              aria-label="開啟命令面板（⌘K）"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: space[2],
                paddingInline: isMobile ? 8 : 12,
                height: 32,
                /**
                 * 響應式寬度：
                 * - mobile：只顯示 Search icon，寬 36
                 * - Pad 直立：縮短到 200，避免擠走 headerLeftExtra
                 * - 其他：320 維持桌面手感
                 */
                minWidth: isMobile ? 36 : isPadPortrait ? 200 : 320,
                width: isMobile ? 36 : undefined,
                background: color.surface.muted,
                border: `1px solid ${color.border.subtle}`,
                borderRadius: radius.md,
                color: color.text.tertiary,
                fontSize: text.sm,
                cursor: 'pointer',
                transition: 'all 120ms cubic-bezier(0.16, 1, 0.3, 1)',
                justifyContent: isMobile ? 'center' : undefined,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = color.border.default;
                e.currentTarget.style.color = color.text.secondary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = color.border.subtle;
                e.currentTarget.style.color = color.text.tertiary;
              }}
            >
              <Search size={13} />
              {!isMobile && <span>搜尋或執行命令</span>}
              {!isMobile && (
                <kbd
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 'auto',
                    fontFamily: font.mono,
                    fontSize: 10.5,
                    lineHeight: 1,
                    color: color.text.muted,
                    background: color.surface.base,
                    border: `1px solid ${color.border.subtle}`,
                    borderRadius: 4,
                    height: 18,
                    minWidth: 26,
                    padding: '0 5px',
                    letterSpacing: tracking.normal,
                    boxSizing: 'border-box',
                  }}
                >
                  ⌘K
                </kbd>
              )}
            </button>
            {headerLeftExtra}
          </div>

          {/* 右側：通知 + 用戶卡 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: space[2],
              flexShrink: 0,
            }}
          >
            {/* 通知鈴鐺：透過 NotificationBell 輪詢真實未讀數量 */}
            <NotificationBell onClick={() => setNotifDrawerOpen(true)} />

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
              <button
                type="button"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  padding: isMobile ? 4 : '4px 10px 4px 4px',
                  borderRadius: radius.full,
                  border: `1px solid ${color.border.subtle}`,
                  background: color.surface.base,
                  outline: 'none',
                  transition: 'all 120ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = color.border.default;
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(15, 23, 42, 0.04)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = color.border.subtle;
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="用戶選單"
              >
                <Avatar
                  style={{
                    /* forest green 漸變取代原 indigo-violet */
                    background: 'linear-gradient(135deg, #156939 0%, #27a05a 100%)',
                    fontSize: text.base,
                    fontWeight: weight.semibold,
                    color: '#ffffff',
                  }}
                  size={28}
                >
                  {currentUser.name?.charAt(0) ?? '?'}
                </Avatar>
                {/* mobile 上隱藏姓名/角色文字，節省 Header 空間 */}
                {!isMobile && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      lineHeight: 1.1,
                    }}
                  >
                    <span
                      style={{
                        fontSize: text.base,
                        color: color.text.primary,
                        fontWeight: weight.semibold,
                      }}
                    >
                      {currentUser.name}
                    </span>
                    {currentUser.role && (
                      <span
                        style={{
                          fontSize: 10,
                          color: color.text.muted,
                          textTransform: 'uppercase',
                          letterSpacing: tracking.widest,
                          fontWeight: weight.semibold,
                          marginTop: 2,
                        }}
                      >
                        {currentUser.role}
                      </span>
                    )}
                  </div>
                )}
              </button>
            </Dropdown>
          </div>
        </Header>

        {/* ─── 頁面內容 ─── */}
        <Content
          style={{
            /**
             * 響應式 padding：mobile 上縮小留出更多內容空間；
             * Pad 直立稍緊湊；其它保持原值。
             */
            padding: isMobile
              ? '12px 12px 24px'
              : isPadPortrait
                ? '16px 16px 24px'
                : '24px 24px 32px',
            minHeight: 'calc(100vh - 56px)',
            background: color.surface.canvas,
          }}
        >
          {children}
        </Content>
      </Layout>

      {/* ─── 全域命令面板（⌘K 觸發，自身管 open 狀態） ─── */}
      <CommandPalette />

      {/* ─── 通知抽屜（受 BaseLayout 控制） ─── */}
      <NotificationDrawer open={notifDrawerOpen} onClose={() => setNotifDrawerOpen(false)} />

      {/* ─── 公告弹窗（每个会话首次进入时弹出未读公告） ─── */}
      <BulletinAnnouncementPopup />
    </Layout>
  );
}
